import type { FC } from "react"
import { useMemo, useReducer, useState } from "react"
import { Responsive, WidthProvider } from "react-grid-layout"
import fp from "lodash/fp"

import type { Span } from "types/spans.types"
import type { SpanPlotDef } from "types/dashboard.types"

import { GRID_LAYOUT_GAP, MD_ROW_HEIGHT, SM_ROW_HEIGHT } from "helpers/utils/constants"
import { layoutReducer } from "helpers/reducers/dashboardReducers"
import { getLayout, sortPlotDefsByLayout } from "helpers/utils/dashboards"
import Message from "widgets/common/Message"
import HoverSyncProvider from "helpers/contexts/HoverSyncProvider"
import DashboardContext from "helpers/contexts/DashboardContext"
import useDateRange from "helpers/hooks/useDateRange"
import CrossEntityPlot from "widgets/plots/CrossEntityPlot"

const GridLayout = WidthProvider(Responsive)

interface SpanDashboardProps {
  span: Span
  config: {
    type: string
    plotDefs: SpanPlotDef[]
  }
}

const SpanDashboard: FC<SpanDashboardProps> = ({ span, config }) => {
  const { arePlotsLoading } = useDateRange()

  const initialLayout = getLayout(config.type)
  const [plotLayout, dispatchPlotLayout] = useReducer(layoutReducer, initialLayout)
  const plotDefs = useMemo(
    () => sortPlotDefsByLayout(config.plotDefs, initialLayout),
    [config.plotDefs, initialLayout],
  )
  const layout = useMemo(() => fp.values(plotLayout), [plotLayout])

  const [rowHeight, setRowHeight] = useState(MD_ROW_HEIGHT)

  const isData = config.plotDefs.some(
    (def) => (plotLayout[def.view.nameKey]?.h ?? 0) !== 0,
  )

  return (
    <DashboardContext.Provider
      value={{
        config,
        entityType: "span" as const,
        plotLayout,
        dispatchPlotLayout,
        rowHeight,
      }}
    >
      <HoverSyncProvider>
        <GridLayout
          style={{
            position: "relative",
            marginRight: `-${GRID_LAYOUT_GAP}px`,
            marginLeft: `-${GRID_LAYOUT_GAP}px`,
          }}
          layouts={{ lg: layout }}
          breakpoints={{
            lg: 1000,
            md: 800,
            sm: 650,
          }}
          cols={{ lg: 12, md: 6, sm: 3 }}
          margin={[GRID_LAYOUT_GAP, GRID_LAYOUT_GAP]}
          rowHeight={rowHeight}
          draggableHandle=".dragIcon"
          onBreakpointChange={(breakpoint: string) =>
            breakpoint === "sm"
              ? setRowHeight(SM_ROW_HEIGHT)
              : setRowHeight(MD_ROW_HEIGHT)
          }
        >
          {plotDefs.map((def: SpanPlotDef) => (
            <div key={def.view.nameKey}>
              <CrossEntityPlot plotDef={def} contextEntity={span} />
            </div>
          ))}
        </GridLayout>
      </HoverSyncProvider>
      {!arePlotsLoading && !isData && (
        <Message messageKey={"generic.NO_DATA_IN_TIME_WINDOW"} />
      )}
    </DashboardContext.Provider>
  )
}

export default SpanDashboard
